import {
  Fragment,
  FunctionComponent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo
} from 'react';

import MetaList from '../MetaList';
import SummaryItem from '../SummaryItem';
import { ForwardProps } from '../../types';
import Flex from '../Flex';
import Icon, { registerIcon } from '../Icon';
import * as caretLeftIcon from '../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import Text from '../Text';
import { Count } from '../Badges';
import BareButton from '../Button/BareButton';
import { StyledPrimary } from '../SummaryItem/SummaryItem';
import { replaceMatchWithElement } from '../../utils';
import { useElement, useI18n, usePrevious, useDirection } from '../../hooks';
import Tooltip from '../Tooltip';

import menuHelpers from './helpers';
import { AcceptedMouseEventElement, MenuItemProps } from './Menu.types';
import MenuContext from './Menu.context';
import {
  StyledAncestors,
  StyledExpandButton,
  StyledItemLabel,
  StyledMenuItem,
  StyledVisibilityHidden
} from './Menu.styles';

registerIcon(caretLeftIcon, caretRightIcon);

const AncestorPath = ({ ancestors = [] }: Pick<MenuItemProps, 'ancestors'>) => {
  const truncatedPath = ancestors.length > 2;
  const pathParts = truncatedPath ? [ancestors[0], ancestors[ancestors.length - 1]] : ancestors;

  const { end } = useDirection();

  return (
    <Flex
      container={{ gap: 0.5, alignItems: 'center' }}
      as={StyledAncestors}
      title={ancestors.map(a => (menuHelpers.isItem(a) ? a.primary : a.label)).join(' > ')}
    >
      {pathParts.map((ancestor, i, arr) => {
        const label = menuHelpers.isItem(ancestor) ? ancestor.primary : ancestor.label;
        return (
          <Fragment key={label}>
            <Text variant='secondary'>{label}</Text>
            {i < arr.length - 1 && (
              <>
                <Text variant='secondary'>
                  <Icon name={`caret-${end}`} />
                </Text>
                {truncatedPath && (
                  <>
                    <Text variant='secondary'>…</Text>
                    <Text variant='secondary'>
                      <Icon name={`caret-${end}`} />
                    </Text>
                  </>
                )}
              </>
            )}
          </Fragment>
        );
      })}
    </Flex>
  );
};

const MenuItem: FunctionComponent<MenuItemProps & ForwardProps> = ({
  id,
  primary,
  secondary,
  ancestors,
  visual,
  count,
  items,
  selected,
  partial,
  href,
  tooltip,
  onClick,
  onExpand,
  disabled,
  ...restProps
}: MenuItemProps & ForwardProps) => {
  const t = useI18n();
  const {
    mode,
    onItemClick,
    accent,
    radioName,
    variant: menuVariant,
    setFocusDescendant,
    getScopedItemId,
    arrowNavigationUnsupported
  } = useContext(MenuContext);
  const previouslySelected = usePrevious(selected);
  const selectionMode = mode === 'single-select' || mode === 'multi-select';
  const isParentItem = useMemo(() => selectionMode && items, [selectionMode, items]);
  const selectableParent = isParentItem && typeof selected === 'boolean';
  const inputRef = useRef<HTMLInputElement>(null);
  const itemId = useMemo(() => getScopedItemId(id), [id, getScopedItemId]);
  const { end } = useDirection();

  const clickHandler = useCallback(
    (e: MouseEvent<AcceptedMouseEventElement>) => {
      const mouseClick = e.detail !== 0;

      if (mouseClick) {
        const target = e.target as HTMLElement;
        const focusAtEl =
          target.getAttribute('role') === 'menuitem'
            ? target
            : target.closest('li[role="menuitem"]');

        if (focusAtEl) setFocusDescendant(focusAtEl as HTMLElement);
      }

      onClick?.(id, e);
      onItemClick?.(id, e);
    },
    [onClick, onItemClick, id]
  );

  const expandHandler = useCallback(
    (e: MouseEvent<AcceptedMouseEventElement>) => {
      onExpand?.(id, e);
    },
    [onExpand, id]
  );

  const navigationInteractionId = `${id}-description`;
  const secondaryId = `${id}-secondary`;

  let accentedPrimary;
  if (accent && !items) {
    const accentRegex = typeof accent === 'function' ? accent(primary) : accent;
    const accentedArr = replaceMatchWithElement(primary, accentRegex, str => (
      <StyledPrimary as='span' isString>
        {str}
      </StyledPrimary>
    ));

    if (accentedArr.length > 1) accentedPrimary = accentedArr;
  }

  const secondaryContent = ancestors ? (
    <AncestorPath ancestors={ancestors} />
  ) : (
    secondary && <MetaList items={secondary} id={secondaryId} wrapItems={false} />
  );

  const [summaryItemRef, setSummaryItemRef] = useElement();

  const summaryItem = (
    <>
      <SummaryItem
        ref={setSummaryItemRef}
        primary={accentedPrimary || (!items ? <Text>{primary}</Text> : primary)}
        secondary={secondaryContent}
        visual={visual}
        actions={
          (items && !selectableParent) || count ? (
            <Flex container={{ gap: 1 }}>
              <Count>{count ?? null}</Count>
              {items && !selectableParent && <Icon name={`caret-${end}`} />}
            </Flex>
          ) : undefined
        }
        container={{
          colGap: 1
        }}
      />
      {tooltip && (
        <Tooltip target={summaryItemRef} hideDelay='none' showDelay='none' portal>
          {tooltip}
        </Tooltip>
      )}
    </>
  );

  let InteractiveWrap;
  if (href) {
    InteractiveWrap = href ? 'a' : BareButton;
  } else if (mode === 'action' || items) {
    InteractiveWrap = BareButton;
  }

  const itemChild = InteractiveWrap ? (
    <InteractiveWrap
      onMouseDown={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.preventDefault();
      }}
      onClick={items ? expandHandler : clickHandler}
      onMouseEnter={menuVariant === 'flyout' ? expandHandler : undefined}
      href={href}
      tabIndex='-1'
      disabled={disabled}
    >
      {summaryItem}
    </InteractiveWrap>
  ) : (
    summaryItem
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !!partial;
    }
  }, [partial]);

  const labelRef = useRef<HTMLLabelElement>(null);

  const itemLabel = useMemo(() => {
    const label = selected ? t('checked_noun', [primary]) : primary;

    return isParentItem ? t('expand_noun', [label]) : label;
  }, [selected, primary, isParentItem]);

  const describedBy = useMemo(() => {
    const hasItems = !!items;
    const hasSecondary = !!secondary;
    let idString = hasSecondary ? secondaryId : undefined;

    if (hasItems) {
      if (hasSecondary) idString = idString!.concat(`, ${navigationInteractionId}`);
      else idString = navigationInteractionId;
    }

    return idString;
  }, [items, secondary, navigationInteractionId, secondaryId]);

  const navigationInstructions = useMemo(() => {
    if (arrowNavigationUnsupported) {
      return t('menu_item_shift_space_expand_collapse');
    }

    return t('menu_item_expand_arrow');
  }, []);

  const interactionNotification = useMemo(() => {
    if (previouslySelected === undefined) return '';

    return `${selected ? t('selected_noun', [primary]) : t('deselected_noun', [primary])}`;
  }, [selected]);

  return (
    <Flex
      {...restProps}
      container={{ alignItems: 'stretch', justify: 'between' }}
      id={itemId}
      as={StyledMenuItem}
      aria-label={itemLabel}
      aria-describedby={describedBy}
      isParentItem={isParentItem}
      role='menuitem'
      aria-disabled={disabled}
      data-expand={!!items}
      tabIndex='-1'
    >
      {selectionMode && typeof selected === 'boolean' ? (
        <>
          <Flex
            ref={labelRef}
            as={StyledItemLabel}
            container={{ alignItems: 'center', gap: 1 }}
            item={{ grow: 1 }}
            onMouseDown={(e: MouseEvent<HTMLLabelElement>) => {
              e.preventDefault();
            }}
            // https://stackoverflow.com/questions/32958091/how-to-prevent-clicking-on-a-checkboxs-label-from-stealing-focus
            onClick={(e: MouseEvent<HTMLLabelElement>) => {
              if (labelRef.current?.control !== e.target) {
                e.preventDefault();

                // Preserve detail property (lost with .click()).
                const event = new window.MouseEvent(e.nativeEvent.type, e.nativeEvent);
                labelRef.current?.control?.dispatchEvent(event);
              }
            }}
          >
            <input
              ref={inputRef}
              type={mode === 'single-select' ? 'radio' : 'checkbox'}
              name={mode === 'single-select' ? radioName : undefined}
              aria-label={primary}
              checked={!!selected}
              disabled={disabled}
              onClick={clickHandler as (e: MouseEvent<HTMLInputElement>) => void}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (mode === 'single-select' && e.key === 'Enter') {
                  e.currentTarget.click();
                }
              }}
              // no-op since React will complain about a controlled input without an onChange
              onChange={() => {}}
              onMouseDown={e => {
                e.preventDefault();
              }}
              tabIndex={-1}
            />
            {(mode === 'single-select' || mode === 'multi-select') && <Icon name='check' />}
            {summaryItem}
          </Flex>

          {isParentItem && (
            <StyledExpandButton icon variant='simple' onClick={expandHandler} tabIndex='-1'>
              <Icon name={`caret-${end}`} />
            </StyledExpandButton>
          )}
        </>
      ) : (
        itemChild
      )}

      {!!items && (
        <StyledVisibilityHidden id={navigationInteractionId}>
          {navigationInstructions}
        </StyledVisibilityHidden>
      )}

      <StyledVisibilityHidden role='alert'>{interactionNotification}</StyledVisibilityHidden>
    </Flex>
  );
};

export default MenuItem;
